import { useEffect, useState } from "react";
import { functionsStore } from "../functions";
import { toast } from "react-toastify";
import Button from "../components/Button";
import Count from "../components/Count";
import Filter from "../components/Filter";
import FilterMenu from "../components/FilterMenu";
import NavHeader from "../components/NavHeader";
import ResponsiveCard from "../components/ResponsiveCard";
import EmptyState from "../components/EmptyState";
import { CarList } from "../data/models";
import React from "react";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { filtersToQueryObject, queryStringToFilters } from "../functions/utils";
import Pagination from "../components/Pagination";
import ColorPicker from "../components/ColorPicker";
import Sort from "../components/Sort";
import VehicleToggle from "../components/VehicleToggle";

import Slider from "@mui/material/Slider";

const LIMIT = Number(process.env.REACT_APP_LIMIT) || 10;

const Homepage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;

  const [data, setData] = useState<CarList>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [carsCount, setCarsCount] = useState<number>(0);
  const [filtersCount, setFiltersCount] = useState<number>(0);
  const [vehicleToggle, setVehicleToggle] = useState<boolean>(false);

  const [tempPriceRange, setTempPriceRange] = useState<string[]>([
    "0",
    "60000",
  ]); // Temporary state for inputs
  const [priceRange, setPriceRange] = useState<number[]>([0, 60000]);

  const [filterOptions, setFilterOptions] = useState({
    "Tip vozila": [],
    "Model vozila": [],
    "Tip goriva": [],
    //"Verzija motora": [],
    "Vrsta mjenjača": [],
    // "Raspon cijene": ["<20.000", "20.000-30.000", "30.000-40.000", ">40.000"],
    Grad: [],
    Boja: [],
  });

  const sortOptions: Record<string, string> = {
    Zadano: "",
    "Najniža cijena": "asc",
    "Najviša cijena": "desc",
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "min" | "max"
  ) => {
    const value = e.target.value;

    // Update the temporary input state directly
    if (type === "min") {
      setTempPriceRange([value, tempPriceRange[1]]);
    } else if (type === "max") {
      setTempPriceRange([tempPriceRange[0], value]);
    }
  };

  const handleInputBlur = (type: "min" | "max") => {
    // Validate and update the slider state on blur
    let [min, max] = tempPriceRange.map((val) => parseInt(val, 10) || 0);

    // Ensure min and max are within valid range
    min = Math.max(0, Math.min(min, max)); // Min cannot exceed Max
    max = Math.min(60000, Math.max(max, min)); // Max cannot be below Min

    setPriceRange([min, max]);
    setTempPriceRange([min.toString(), max.toString()]); // Sync temp input state with validated values
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setPriceRange(newValue as number[]);
    setTempPriceRange((newValue as number[]).map(String)); // Sync temporary input state with slider changes
  };

  useEffect(() => {
    const filtersFromURL = queryStringToFilters(searchParams);
    const hasAnyFilters = Object.keys(filtersFromURL).length > 0;
    const sort = searchParams.get("sort") || "";
    const page = searchParams.get("page") || "1";

    // If no filters and no sort, just fetch empty
    if (!hasAnyFilters && !sort) {
      setSearchParams({ page: "1" });
      fetchData({}, 1, vehicleToggle);
    } else {
      setSelectedFilters(filtersFromURL);
      fetchData(filtersFromURL, 1, vehicleToggle);
    }
  }, [searchParams, currentPage, vehicleToggle]);

  const fetchData = async (
    filters?: Record<string, string[]>,
    page: number = 1,
    vehicleToggle: boolean = true
  ) => {
    try {
      setLoading(true);
      const sort = searchParams.get("sort") || "";
      const updatedFilters = { ...filters };

      const response = await functionsStore.getCars(
        updatedFilters,
        sort,
        currentPage,
        vehicleToggle
      );

      if (response) {
        setData(response.results.vehicles);
        setCarsCount(response["count"]);

        setFilterOptions((prevOptions) => ({
          ...prevOptions,
          "Tip vozila": response.results.dropdowns.type_hr || [],
          "Model vozila": response.results.dropdowns.family || [],
          "Tip goriva": response.results.dropdowns.fuel_hr.sort() || [],
          //"Verzija motora": response.results.dropdowns.model_engine.sort() || [],
          "Vrsta mjenjača": response.results.dropdowns.gear_type.sort() || [],
          Grad: response.results.dropdowns.dealer_city.sort() || [],
          Boja: response.results.dropdowns.color_f.sort() || [],
        }));

        if (filters) {
          const filterKeys = [
            "Tip vozila",
            "Model vozila",
            "Tip goriva",
            "Vrsta mjenjača",
            "rasponcijene",
            "Grad",
            "Boja",
          ];

          const totalFiltersCount = Object.entries(filters).filter(
            ([key, values]) =>
              filterKeys.includes(key) &&
              values !== undefined &&
              values.length > 0
          ).length;
          setFiltersCount(totalFiltersCount);
        }
      } else {
        toast.warn("Nema rezultata koji odgovaraju filterima.");
        setData([]);
      }
    } catch (err) {
      toast.error("Došlo je do pogreške pri dohvaćanju podataka.");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (filters: Record<string, string[]>) => {
    const totalFiltersCount = Object.entries(filters).filter(
      ([key, values]) => key !== "sort" && key !== "page" && values.length > 0
    ).length;

    setFiltersCount(totalFiltersCount); // Update filter count state
    fetchData(filters, 1); // Fetch data with updated filters
  };

  const applyFilters = async () => {
    const updatedFilters = {
      ...selectedFilters,
      rasponcijene: [`${priceRange[0]}`, `${priceRange[1]}`], // Pass min and max as strings
    };

    const queryParams = {
      ...filtersToQueryObject(updatedFilters),
      page: "1",
    };

    setSearchParams(queryParams);
    fetchData(updatedFilters, 1);
  };

  const resetFilters = () => {
    if (Object.keys(selectedFilters).length > 1) {
      setSelectedFilters({});
      setFiltersCount(0);
      setSearchParams({ page: "1" });
      setData([]);

      setPriceRange([0, 60000]);
      setTempPriceRange(["0", "60000"]);
    }
  };

  const handleFilterChange = (name: string, values: string[]) => {
    // Kreiraj nove filtere odmah
    const newFilters = {
      ...selectedFilters,
      [name]: values,
    };

    // Ukloni filter ako je prazan
    if (values.length === 0) {
      delete newFilters[name];
    }

    // Pretvori nove filtere u query parametre
    const queryParams = {
      ...filtersToQueryObject(newFilters),
      page: "1",
    };

    setSearchParams(queryParams);
    setSelectedFilters(newFilters);
  };

  const changePage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (selectedFilters.rasponcijene) {
      const [min, max] = selectedFilters.rasponcijene.map((value) =>
        parseInt(value, 10)
      );
      setPriceRange([min, max]);
      setTempPriceRange([String(min), String(max)]);
    }
  }, [selectedFilters]);

  return (
    <div className="flex h-full min-h-screen ">
      {/* Sidebar for desktop */}
      <div className="flex-col gap-3 px-6 py-6 w-[286px] bg-gray-extra-light hidden lg:flex">
        <h2 className="text-lg text-gray-extra-dark">Filtrirajte</h2>
        <div className="flex flex-col gap-3">
          {Object.entries(filterOptions)
            .filter(([filterName]) => filterName !== "Boja")
            .map(([filterName, options]) => (
              <Filter
                key={filterName}
                name={filterName}
                data={options}
                selected={selectedFilters[filterName] || []} // Pass current selections
                onChange={(values) => handleFilterChange(filterName, values)} // Handle updated values directly
              />
            ))}
        </div>

        <hr className="w-full border-t-1 border-extra-light" />

        <div className="flex flex-col gap-1">
          {/* Input Fields */}
          <div className="flex items-center gap-2">
            <div>
              <label htmlFor="minPrice" className="mb-1 text-xs text-gray-400">
                Min. cijena
              </label>
              <input
                id="minPrice"
                type="number"
                className="inline-flex justify-between w-full p-3 text-sm border rounded appearance-none focus:border-primary-blue focus:outline-none text-gray-extra-dark"
                value={tempPriceRange[0]} // Bind to temporary state
                onChange={(e) => handleInputChange(e, "min")}
                onBlur={() => handleInputBlur("min")} // Update state on blur
                min={0}
                max={priceRange[1]}
              />
            </div>
            <div>
              <label htmlFor="maxPrice" className="mb-1 text-xs text-gray-400">
                Max. cijena
              </label>
              <input
                id="maxPrice"
                type="number"
                className="inline-flex justify-between w-full p-3 text-sm border rounded appearance-none focus:border-primary-blue focus:outline-none text-gray-extra-dark"
                value={tempPriceRange[1]} // Bind to temporary state
                onChange={(e) => handleInputChange(e, "max")}
                onBlur={() => handleInputBlur("max")} // Update state on blur
                min={priceRange[0]}
                max={60000}
              />
            </div>
          </div>

          {/* Slider */}
          <Slider
            value={priceRange}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            min={0}
            max={60000}
            step={1000}
            marks={[
              { value: 0, label: "0" },
              { value: 30000, label: "30.000" },
              { value: 60000, label: "60.000" },
            ]}
          />
        </div>

        <hr className="w-full border-t-1 border-extra-light" />
        <ColorPicker
          colors={filterOptions.Boja}
          selected={selectedFilters.Boja || []}
          onChange={(values) => handleFilterChange("Boja", values)}
        />
        <hr className="w-full border-t-1 border-extra-light" />
        <div className="flex flex-col gap-2">
          <Button
            text="Filtrirajte"
            variant="primary"
            size="base"
            width="w-full"
            action={applyFilters}
          />
          <Button
            text="Očistite filter"
            variant="secondary"
            size="base"
            width="w-full"
            action={resetFilters}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col flex-1 gap-6 py-8 sm:px-6">
        {/* NavHeader remains intact */}
        <NavHeader gap="gap-3" className="flex lg:flex-row">
          <div className="flex gap-2.5 items-center whitespace-nowrap">
            <h4 className="text-base md:text-lg lg:text-[18px] font-bold uppercase">
              Trenutno dostupno
            </h4>
            <Count count={carsCount} size="base" />
          </div>

          <div className="flex flex-col gap-2 sm:grid sm:grid-cols-[1fr_2fr] lg:flex sm:flex-row">
            {/* vozila u dolasku */}
            <VehicleToggle
              vehicleToggle={vehicleToggle}
              setVehicleToggle={setVehicleToggle}
              className="flex flex-2"
            />
            <div className="flex gap-2">
              {/* sortirajte */}
              <Sort sortOptions={sortOptions} className="w-full" />
              {/* onSortApply={handleSort} */}
              {/* Filtriraje */}
              <FilterMenu
                className="flex w-full lg:hidden"
                filterData={filterOptions}
                onFilterApply={(filters) => {
                  setSelectedFilters(filters);

                  const totalFiltersCount = Object.entries(filters).filter(
                    ([key, values]) =>
                      key !== "sort" && key !== "page" && values.length > 0
                  ).length;

                  setFiltersCount(totalFiltersCount);
                  fetchData(filters, 1);
                }}
                selectedFilters={selectedFilters}
                handleFilterChange={handleFilterChange}
                resetFilters={resetFilters}
                filtersCount={filtersCount}
                setSearchParams={setSearchParams} // No casting necessary
              />
            </div>
          </div>
        </NavHeader>

        {/* Main content */}

        {/* Poruka za učitavanje podataka na vrhu */}
        {loading && <p className="text-center">Učitavanje podataka...</p>}
        {/* Cards */}
        {data.length === 0 && !loading ? (
          <EmptyState
            title="Nema rezultata"
            message="Nažalost, trenutno nema dostupnih automobila koji odgovaraju odabranim filterima."
          />
        ) : !loading ? (
          <>
            {data.map((item, index) => (
              <React.Fragment key={`${item.id}-${index}`}>
                {index !== 0 && (
                  <hr className="w-full border-t-1 border-extra-light" />
                )}
                <ResponsiveCard car={item} />
              </React.Fragment>
            ))}
          </>
        ) : (
          <></>
        )}

        {carsCount > LIMIT && !loading && (
          <>
            <hr className="w-full border-t-1 border-extra-light" />
            <Pagination
              page={currentPage}
              carsCount={carsCount}
              changePage={changePage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Homepage;
